/** Shared lookups most screens need, cached per company session. */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface Account {
  id: string;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  subType: string;
  parentId: string | null;
  isPosting: boolean;
  isControl: boolean;
  isTaxAccount: boolean;
  isCash: boolean;
  systemRole: string | null;
  description: string;
  active: boolean;
  inUse: boolean;
}

export interface DimensionValue {
  id: string;
  dimensionId: string;
  code: string;
  name: string;
  active: boolean;
}

export interface Dimension {
  id: string;
  code: string;
  name: string;
  active: boolean;
  values: DimensionValue[];
}

export interface Period {
  id: string;
  number: number;
  name: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'SOFT_LOCKED' | 'HARD_LOCKED';
  lockedAt: string | null;
}

export interface FiscalYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'OPEN' | 'CLOSED';
  periods: Period[];
}

export interface Branch {
  id: string;
  code: string;
  name: string;
  isHeadOffice: boolean;
  street: string;
  barangay: string;
  city: string;
  province: string;
  zip: string;
  rdoCode: string;
  active: boolean;
}

export function useAccounts() {
  return useQuery({ queryKey: ['accounts'], queryFn: () => api<Account[]>('/accounts') });
}

export function useDimensions() {
  return useQuery({ queryKey: ['dimensions'], queryFn: () => api<Dimension[]>('/dimensions') });
}

export function useFiscalYears() {
  return useQuery({ queryKey: ['fiscal-years'], queryFn: () => api<FiscalYear[]>('/fiscal-years') });
}

export function useBranches() {
  return useQuery({ queryKey: ['branches'], queryFn: () => api<Branch[]>('/company/branches') });
}

export const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const;

export const TYPE_LABEL: Record<Account['type'], string> = {
  ASSET: 'Assets',
  LIABILITY: 'Liabilities',
  EQUITY: 'Equity',
  INCOME: 'Income',
  EXPENSE: 'Expenses',
};

/** Today's business date in Manila, YYYY-MM-DD. */
export function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export interface TaxCode {
  id: string;
  code: string;
  name: string;
  kind: 'VAT' | 'PERCENTAGE' | 'NONE';
  treatment: 'VATABLE' | 'EXEMPT' | 'ZERO_RATED' | 'GOVERNMENT' | 'NOT_APPLICABLE';
  rate: string;
  withholdingVatRate: string;
}

export interface Atc {
  id: string;
  code: string;
  description: string;
  kind: 'EWT' | 'FWT';
  payeeType: 'INDIVIDUAL' | 'CORPORATE';
  rate: string;
  active: boolean;
}

export interface Contact {
  id: string;
  code: string;
  isCustomer: boolean;
  isSupplier: boolean;
  registeredName: string;
  tradeName: string;
  payeeType: 'INDIVIDUAL' | 'CORPORATE';
  tin: string | null;
  tinBranchCode: string;
  tinFormatted: string | null;
  vatStatus: 'VAT' | 'NON_VAT' | 'VAT_EXEMPT';
  isGovernment: boolean;
  street: string;
  barangay: string;
  city: string;
  province: string;
  zip: string;
  address: string;
  email: string;
  phone: string;
  contactPerson: string;
  defaultTaxCode: string | null;
  expectedCwtAtc: string | null;
  defaultEwtAtc: string | null;
  defaultExpenseAccountId: string | null;
  paymentTermsDays: number;
  creditLimit: string | null;
  bankName: string;
  bankAccountMasked: string | null;
  active: boolean;
  rowVersion: number;
}

export interface Item {
  id: string;
  code: string;
  name: string;
  type: 'SERVICE' | 'NON_INVENTORY' | 'INVENTORY';
  uom: string;
  salesPrice: string;
  purchasePrice: string;
  salesAccountId: string | null;
  expenseAccountId: string | null;
  salesTaxCode: string | null;
  purchaseTaxCode: string | null;
  ewtAtc: string | null;
  active: boolean;
}

export function useTaxCodes() {
  return useQuery({ queryKey: ['tax-codes'], queryFn: () => api<TaxCode[]>('/tax-codes'), staleTime: 300_000 });
}

export function useAtcs() {
  return useQuery({ queryKey: ['atcs'], queryFn: () => api<Atc[]>('/atcs'), staleTime: 300_000 });
}

export function useContacts(role: 'customer' | 'supplier') {
  return useQuery({
    queryKey: ['contacts', role, 'all'],
    queryFn: () => api<{ data: Contact[] }>(`/contacts?role=${role}&active=true&pageSize=500`).then((r) => r.data),
  });
}

export function useItems() {
  return useQuery({ queryKey: ['items'], queryFn: () => api<Item[]>('/items') });
}
